import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Check } from 'lucide-react';

const RegisterPage = () => {
  const { registerWithEmail, loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      toast.success('Account created! Let\'s set up your profile.');
      navigate('/onboarding');
    } catch (error) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        toast.error('Google sign-up failed. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.displayName || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(formData.password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      toast.error('Password must contain at least one special character');
      return;
    }
    
    setLoading(true);
    try {
      await registerWithEmail(formData.email, formData.password, formData.displayName);
      toast.success('Account created! Let\'s set up your profile.');
      navigate('/onboarding');
    } catch (error) {
      const code = error?.code;
      if (code === 'auth/email-already-in-use') {
        toast.error('An account with this email already exists.');
      } else if (code === 'auth/weak-password') {
        toast.error('Password is too weak. Please use at least 6 characters.');
      } else if (code === 'auth/invalid-email') {
        toast.error('Please enter a valid email address.');
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Connect with golfers nationwide',
    'Track your rounds and scores',
    'Find events and tournaments',
    'Join city networks and chat rooms',
    'Access AI-powered golf assistant'
  ];

  return (
    <div className="min-h-screen bg-deep-green pt-20 flex">
      {/* Left Side - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
        <div className="max-w-md">
          <h2 className="font-display font-bold text-3xl text-white mb-6">
            Join the Nation's Premier Minority Golfers Network
          </h2>
          <p className="text-cream/80 font-body mb-8">
            Create your free account and unlock the full potential of Fairway Alliance.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-center gap-3 text-cream/90">
                <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4 text-deep-green" />
                </div>
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8 lg:hidden">
            <img 
              src="/logo.png" 
              alt="Fairway Alliance"
              className="h-16 w-auto mx-auto mb-3"
            />
          </div>
          
          <h1 className="font-display font-bold text-2xl text-white mb-2">Create Your Account</h1>
          <p className="text-cream/70 font-body mb-8">Join thousands of golfers on Fairway Alliance</p>

          {/* Google Sign Up */}
          <Button
            type="button"
            variant="outline"
            className="w-full py-6 mb-6 bg-white hover:bg-gray-50 text-gray-800 border-white/20 font-semibold"
            onClick={handleGoogleSignUp}
            disabled={googleLoading}
            data-testid="register-google"
          >
            {googleLoading ? (
              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            ) : (
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            Continue with Google
          </Button>

          {/* Apple Sign Up */}
          <Button
            type="button"
            variant="outline"
            className="w-full py-6 mb-6 bg-black hover:bg-gray-900 text-white border-white/20 font-semibold"
            onClick={async () => {
              setAppleLoading(true);
              try {
                await loginWithApple();
                toast.success('Account created! Let\'s set up your profile.');
                navigate('/onboarding');
              } catch (error) {
                if (error?.code !== 'auth/popup-closed-by-user') {
                  toast.error('Apple sign-up failed. Please try again.');
                }
              } finally { setAppleLoading(false); }
            }}
            disabled={appleLoading}
            data-testid="register-apple"
          >
            {appleLoading ? (
              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            ) : (
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
            )}
            Continue with Apple
          </Button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/20" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-deep-green text-cream/50">or register with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-cream/90">Full Name</Label>
              <Input
                id="displayName"
                name="displayName"
                type="text"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="John Smith"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-gold"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-cream/90">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-gold"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-cream/90">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-gold pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-cream/90">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-gold"
                data-testid="register-confirm-password"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full btn-gold py-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            <p className="text-cream/50 text-xs text-center mt-3">
              By creating an account, you agree to our{' '}
              <a href="https://fairwayalliance.org/terms" className="text-gold hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="https://fairwayalliance.org/privacy" className="text-gold hover:underline">Privacy Policy</a>.
            </p>
          </form>

          {/* Login Link */}
          <p className="text-center mt-8 text-cream/70 font-body">
            Already have an account?{' '}
            <Link to="/login" className="text-gold hover:underline font-semibold">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
